<template>
  <div class="relative flex flex-col gap-4">
    <h2>{{ t('upgradeExtras') }}</h2>
    <div class="flex flex-col gap-4">
      <div
        v-for="extra in sortedExtras"
        v-show="!isChildSeat(extra.extra.type) || showChildSeatsState || hasBookedChildSeat(extra)"
        :key="extra.extra.id"
        class="flex flex-col gap-1"
        :data-testid="`upgrade-extra-${extra.extra.type}`"
      >
        <div class="flex gap-2">
          <p
            class="font-medium"
            :class="{
              'text-success': extra.isBooked || extra.customExtra,
              'text-red-700': !extra.isBooked && !extra.customExtra,
            }"
          >
            {{ extra.extraName }} - {{ extra.priceLabel }}
            {{ extra.isBooked || extra.customExtra ? '✓' : '✕' }}
          </p>
          <Information v-if="extra.extra.type === VehicleExtraType.CROSSBORDER"
            ><template #modal>{{ crossborderCountriesLabel }}</template></Information
          >
          <Information v-if="extra.extra.type === VehicleExtraType.EXTRA_KM"
            ><template #modal>{{
              t('extrasKmInfo', {
                price: formatCurrency(booking.priceCalculation.pricePerExtraKm),
              })
            }}</template></Information
          >
        </div>
        <div class="-mx-2 flex gap-2 overflow-y-auto p-2">
          <button
            v-for="button in extra.buttons"
            v-show="!extra.customExtra"
            :key="button.option.id"
            class="flex flex-shrink-0 flex-col justify-between gap-2 overflow-hidden rounded-md px-3 py-2 md:w-32"
            :class="{
              'bg-white': !button.isBookedOption,
              'bg-hazel-100': button.isBookedOption,
              'ring ring-ring ring-offset-2':
                button.isBookedOption &&
                !changedBookedExtraTypes[extra.extra.type]?.vehicleExtraOptionId &&
                !(extra.extra.type === VehicleExtraType.EXTRA_KM && customKmValue !== 0) &&
                !extra.customExtra,
              '!bg-black text-white ring ring-ring ring-offset-2':
                !button.isBookedOption &&
                changedBookedExtraTypes[extra.extra.type]?.vehicleExtraOptionId ===
                  button.option.id,
              'hover:bg-hazel-100':
                !disabled &&
                !button.isBookedOption &&
                changedBookedExtraTypes[extra.extra.type]?.vehicleExtraOptionId !==
                  button.option.id,
            }"
            :disabled="
              disabled || (button.isBookedOption && !changedBookedExtraTypes[extra.extra.type])
            "
            @click.prevent="selectOption(extra, button, $event.target as HTMLButtonElement)"
          >
            <div class="text-base font-medium">{{ button.title }}</div>
            <div class="text-xs">
              {{ !button.isBookedOption ? '+ ' : '' }}{{ button.priceLabel }}
            </div>
          </button>

          <!-- "Individual" tile for existing custom extra (non-EXTRA_KM types) -->
          <div
            v-if="
              extra.customExtra &&
              extra.extra.type !== VehicleExtraType.EXTRA_KM &&
              !changedBookedExtraTypes[extra.extra.type]
            "
            class="flex flex-shrink-0 flex-col justify-between gap-2 overflow-hidden rounded-md bg-hazel-100 px-3 py-2 md:w-32"
            :class="{
              'ring ring-ring ring-offset-2':
                !changedBookedExtraTypes[extra.extra.type]?.vehicleExtraOptionId,
            }"
          >
            <div class="text-base font-medium">{{ t('individual') }}</div>
            <div class="text-xs">
              {{ getCustomExtraLabel(extra.customExtra) }}
            </div>
            <div class="text-xs font-medium">{{ formatCurrency(extra.customExtra.price) }}</div>
          </div>

          <!-- Custom Extra KM Input for values above max standard package -->
          <div
            v-if="extra.extra.type === VehicleExtraType.EXTRA_KM && maxExtraKmOption"
            ref="custom-km-tile"
            class="flex flex-shrink-0 flex-col justify-between gap-1 overflow-hidden rounded-md px-3 py-2"
            :class="{
              'bg-hazel-100': extra.customExtra,
              'ring ring-ring ring-offset-2':
                (extra.customExtra || customKmValue !== 0) &&
                !changedBookedExtraTypes[extra.extra.type]?.vehicleExtraOptionId,
              'bg-white': !extra.customExtra,
            }"
          >
            <Input
              v-model="customKmInput"
              type="number"
              :min="maxExtraKmValue + 1"
              :placeholder="`>${maxExtraKmOption.value}`"
              adornment="km"
              class="w-32"
              :disabled="disabled"
              @input="handleCustomKmInput"
              @blur="handleCustomKmInputBlur"
            />
            <div v-if="customKmValue > 0" class="text-xs font-medium">
              +{{ formatCurrency(displayedCustomKmPrice) }}
            </div>
            <div
              v-else-if="customKmInput && customKmInput <= maxExtraKmValue"
              class="text-xs text-red-500"
            >
              {{ t('customKmMinError', { min: maxExtraKmValue + 1 }) }}
            </div>
          </div>
        </div>
      </div>

      <CVButton
        v-if="hasHiddenChildSeats && !showChildSeatsState"
        outline
        @click.prevent="showAllChildSeats"
      >
        {{ t('showChildSeats') }}
      </CVButton>
    </div>

    <p v-if="hasChanged && isRecalculating" class="font-medium">
      {{ t('recalculatingPrice') }}
    </p>

    <p v-else-if="newPricing" class="font-medium">
      {{ t('newPrice') }}:
      <span v-currency="newPricing?.totalPrice"></span>
    </p>

    <div v-if="hasChanged" class="col-span-3 flex">
      <CVButton
        variant="default"
        :disabled="isBusy"
        :is-loading="isBusy"
        class="mr-2"
        @click.prevent="saveExtrasChange"
      >
        {{ t('save') }}
      </CVButton>
      <CVButton variant="ghost" :disabled="isBusy" class="mr-2" @click.prevent="cancelExtrasChange">
        {{ t('cancel') }}
      </CVButton>
    </div>

    <div v-if="isBusy" class="absolute -inset-2 flex items-center justify-center bg-black/20">
      <Spinner />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getBookedExtras, getCustomBookedExtras } from '@/utils/pricing.utils';
import type { Booking } from '@/entities/bookings/booking.entity';
import { type ChangedBookedExtraTypes } from '@/components/price-change-confirmation/price-change-confirmation';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { formatCurrency } from '@/utils/format-numbers';
import { computed, ref, useTemplateRef, watch } from 'vue';
import type { CustomBookedExtra } from '@/entities/bookings/booked-extra';
import { useI18n } from 'vue-i18n';
import Spinner from '../icons/Spinner.vue';
import { debounce, pickBy } from 'lodash';
import Information from '../Information.vue';
import { isBiggerExtraOption } from '@/utils/is-bigger-extra-option';
import confetti from 'canvas-confetti';
import CVButton from '@/components/buttons/CVButton.vue';
import Input from '@/components/Input.vue';
import { formatCountryCodes, getCrossborderInfoCountries } from '@/utils/crossborder-countries';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import {
  type BookedExtraDto,
  PriceRecalculationMode,
  type UpdateCustomBookedExtraDto,
  type VehicleExtra,
  type VehicleExtraOption,
  VehicleExtraType,
  CounterpartyType,
} from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';

const props = defineProps<{
  booking: Booking;
  disabled?: boolean;
  handoverAdjustment?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t, locale } = useI18n();

const crossborderCountriesLabel = computed(() => {
  const countries = getCrossborderInfoCountries(newPricing.value ?? props.booking.priceCalculation);
  return t('crossBorderInfoDynamic', {
    countries: formatCountryCodes(
      countries,
      newPricing.value ?? props.booking.priceCalculation,
      locale.value,
    ),
  });
});

const newPricing = ref<Pricing | null>(null);
const { isPending: isRecalculating, mutateAsync: updateBooking } = useUpdateBooking();
const { confirmAndSave, isBusy: isPriceBusy } = usePriceChangeConfirmation();
const isBusy = computed(() => isRecalculating.value || isPriceBusy.value);

const changedBookedExtraTypes = ref<ChangedBookedExtraTypes>({});
const customBookedExtras = ref<UpdateCustomBookedExtraDto[] | undefined>(undefined);

const showChildSeatsState = ref(false);

const customKmInput = ref<number | null>(null);
const customKmValue = ref(0);

watch(
  () => [
    getBookedExtras(props.booking.priceCalculation),
    getCustomBookedExtras(props.booking.priceCalculation),
  ],
  () => {
    changedBookedExtraTypes.value = {};
    newPricing.value = null;
    customBookedExtras.value = undefined;
  },
  { deep: true },
);

const existingCustomExtraKm = computed(() => {
  return getCustomBookedExtras(props.booking.priceCalculation)?.find(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.EXTRA_KM && !extra.isAdditional,
  );
});

const maxExtraKmOption = computed(() =>
  props.booking.priceCalculation.availableExtras
    ?.find((extra) => extra.type === VehicleExtraType.EXTRA_KM)
    ?.options.at(-1),
);

const maxExtraKmValue = computed(() => Number(maxExtraKmOption.value?.value ?? 0));

watch(
  [() => getCustomBookedExtras(props.booking.priceCalculation), maxExtraKmOption],
  () => {
    if (
      existingCustomExtraKm.value?.value &&
      existingCustomExtraKm.value.value > maxExtraKmValue.value
    ) {
      customKmInput.value = existingCustomExtraKm.value.value;
      customKmValue.value = existingCustomExtraKm.value.value;
    } else {
      customKmInput.value = null;
      customKmValue.value = 0;
    }
  },
  { immediate: true },
);

const extraKmPricePerKm = computed(() => {
  if (!maxExtraKmOption.value || maxExtraKmValue.value === 0) return 0;
  return (maxExtraKmOption.value.calculatedPrice ?? 0) / maxExtraKmValue.value;
});

const customKmPrice = computed(() => {
  if (customKmValue.value <= 0) return 0;
  return Math.round(extraKmPricePerKm.value * customKmValue.value * 100) / 100;
});

const displayedCustomKmPrice = computed(() => {
  if (customKmValue.value <= 0) return 0;
  const isValueUnchanged = customKmValue.value === existingCustomExtraKm.value?.value;
  return isValueUnchanged
    ? (existingCustomExtraKm.value?.price ?? customKmPrice.value)
    : customKmPrice.value;
});

function fireConfetti(element: HTMLElement | undefined | null) {
  if (!element) return;
  const { top, left, right, bottom } = element.getBoundingClientRect();
  confetti({
    origin: {
      x: (left + (right - left) / 2) / window.innerWidth,
      y: (top + (bottom - top) / 2) / window.innerHeight,
    },
    colors: ['#CDAF74'],
  });
}
const customKmTile = useTemplateRef<HTMLDivElement[]>('custom-km-tile');
const handleCustomKmInput = debounce(async () => {
  const inputValue = customKmInput.value;
  const minCustomKm = maxExtraKmValue.value + 1;

  if (inputValue && inputValue >= minCustomKm) {
    customKmValue.value = inputValue;
    // Clear any selected standard option for EXTRA_KM
    changedBookedExtraTypes.value[VehicleExtraType.EXTRA_KM] = undefined;
    changedBookedExtraTypes.value = pickBy(
      changedBookedExtraTypes.value,
      (value) => value !== undefined,
    );
    await recalculate();
    fireConfetti(customKmTile.value?.[0]);
  } else {
    customKmValue.value = 0;
    await recalculate();
  }
}, 1000);

const handleCustomKmInputBlur = async () => {
  handleCustomKmInput.flush();
  if (customKmValue.value <= 0) {
    customKmInput.value = null;
  }
  await recalculate();
};

const isChildSeat = (extraType: string) => {
  return (
    extraType === VehicleExtraType.BABY_SEAT ||
    extraType === VehicleExtraType.CHILD_SEAT_0 ||
    extraType === VehicleExtraType.CHILD_SEAT_1 ||
    extraType === VehicleExtraType.BOOSTER_SEAT
  );
};

const extraUpdates = computed(() => {
  return Object.values(changedBookedExtraTypes.value).filter(
    (value) => value !== undefined,
  ) as BookedExtraDto[];
});

const hasBookedChildSeat = (extra: (typeof extrasUpgradeData.value)[number]) => {
  return isChildSeat(extra.extra.type) && extra.isBooked;
};

const hasHiddenChildSeats = computed(() => {
  return extrasUpgradeData.value.some(
    (extra) => isChildSeat(extra.extra.type) && !hasBookedChildSeat(extra),
  );
});

const showAllChildSeats = () => {
  showChildSeatsState.value = true;
};

const hasChanged = computed(() => {
  if (newPricing.value !== null) return true;

  const hasChangedExtras = Object.keys(changedBookedExtraTypes.value).length > 0;
  const hasCustomKmChange =
    customKmValue.value > 0 && customKmValue.value !== existingCustomExtraKm.value?.value;
  return hasChangedExtras || hasCustomKmChange;
});

const isGlassTyreWaiverAndMobilityIncluded = computed(() => {
  const deductibleChange = changedBookedExtraTypes.value[VehicleExtraType.DEDUCTIBLE];
  const deductibleValue = deductibleChange?.vehicleExtraOptionId
    ? props.booking.priceCalculation.availableExtras
        ?.find((extra) => extra.type === VehicleExtraType.DEDUCTIBLE)
        ?.options.find((option) => option.id === deductibleChange.vehicleExtraOptionId)?.value
    : getBookedExtras(props.booking.priceCalculation)?.find(
        (bookedExtra) => bookedExtra.vehicleExtraSnapshot.type === VehicleExtraType.DEDUCTIBLE,
      )?.vehicleExtraOptionSnapshot.value;
  return deductibleValue === '0';
});

const recalculate = async () => {
  const hasValidCustomKmSelection =
    customKmValue.value !== existingCustomExtraKm.value?.value &&
    customKmInput.value !== null &&
    customKmValue.value > maxExtraKmValue.value;

  if (hasValidCustomKmSelection) {
    customBookedExtras.value = [
      ...(getCustomBookedExtras(props.booking.priceCalculation)?.filter(
        (extra) => extra.customVehicleExtraType !== VehicleExtraType.EXTRA_KM || extra.isAdditional,
      ) ?? []),
      {
        description: t('customExtraKmDescription', { km: customKmValue.value }),
        price: customKmPrice.value,
        customVehicleExtraType: VehicleExtraType.EXTRA_KM,
        value: customKmValue.value,
      },
    ];
  } else {
    customBookedExtras.value = undefined;
  }

  if (!hasValidCustomKmSelection && !Object.entries(changedBookedExtraTypes.value).length) {
    newPricing.value = null;
    return;
  }

  const result = await updateBooking({
    id: props.booking.id,
    booking: {
      pricing: {
        bookedExtras: extraUpdates.value,
        customBookedExtras: customBookedExtras.value,
        priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
      },
      dryRun: true,
    },
    ...(props.handoverAdjustment ? { query: { handoverAdjustment: true as const } } : {}),
  });
  newPricing.value = result?.priceCalculation ?? null;
};

const sortedExtras = computed(() => {
  return [...extrasUpgradeData.value].sort((a, b) => {
    return (isChildSeat(a.extra.type) ? 1 : 0) - (isChildSeat(b.extra.type) ? 1 : 0);
  });
});

const extrasUpgradeData = computed(() => {
  return props.booking.priceCalculation
    .availableExtras!.filter((extra) => extra.type !== VehicleExtraType.YOUNG_DRIVER)
    .map((extra) => {
      const bookedExtra = getBookedExtras(props.booking.priceCalculation)?.find(
        (be) => be.vehicleExtraSnapshot.type === extra.type,
      );
      const bookedOption = bookedExtra?.vehicleExtraOptionSnapshot ?? extra.options[0];
      const options = extra.options.filter((option) => {
        return isBiggerExtraOption(option, bookedOption, extra);
      });
      options.unshift(bookedOption);

      const { extraName, priceLabel } = getOptionData(extra, bookedOption);
      const customExtra = getCustomBookedExtras(props.booking.priceCalculation)?.find(
        (cbe) => cbe.customVehicleExtraType === extra.type && !cbe.isAdditional,
      );
      const isIncluded =
        extra.type === VehicleExtraType.GLASS_TYRE_WAIVER_AND_MOBILITY &&
        isGlassTyreWaiverAndMobilityIncluded.value;
      const buttons = isIncluded
        ? [
            {
              option: extra.options.find((option) => option.value === 'true') ?? bookedOption,
              title: t('yes'),
              priceLabel: t('included'),
              isBookedOption: true,
            },
          ]
        : options.map((option) => {
            const isBookedOption =
              !customExtra &&
              (bookedExtra
                ? option.id === bookedExtra.vehicleExtraOptionSnapshot.id
                : option.id === extra.options[0]?.id);
            const { title, priceLabel } = getOptionData(
              extra,
              option,
              isBookedOption ? 0 : (bookedExtra?.calculatedPrice ?? 0),
            );
            return {
              option,
              title,
              priceLabel,
              isBookedOption,
            };
          });

      return {
        extra,
        extraName,
        priceLabel: isIncluded ? t('included') : priceLabel,
        isBooked: isIncluded || (bookedExtra?.calculatedPrice ?? 0) > 0,
        customExtra,
        buttons,
      };
    });
});

const getOptionData = (extra: VehicleExtra, option: VehicleExtraOption, bookedPrice = 0) => {
  const price = option.calculatedPrice ?? 0;

  const notPerDay = extra.type === VehicleExtraType.EXTRA_KM;

  const priceLabel = notPerDay
    ? `${formatCurrency(price - bookedPrice)}`
    : `${formatCurrency((price - bookedPrice) / (props.booking.priceCalculation.days ?? 1))}/${t('day')}`;

  const title = `${
    option.value === 'true'
      ? t('yes')
      : option.value === 'false'
        ? t('no')
        : option.name.length > 0
          ? option.name
          : option.value
  }`;
  return {
    extraName: t(`vehicleExtras.${extra.type}`),
    title,
    priceLabel,
  };
};

const getCustomExtraLabel = (customExtra: CustomBookedExtra) => {
  if (customExtra.value !== undefined) {
    if (customExtra.customVehicleExtraType === VehicleExtraType.EXTRA_KM) {
      return `${customExtra.value} km`;
    }
    return String(customExtra.value);
  }
  return customExtra.description;
};

const selectOption = async (
  { extra, customExtra }: (typeof extrasUpgradeData.value)[number],
  { option, isBookedOption }: (typeof extrasUpgradeData.value)[number]['buttons'][number],
  buttonElement: HTMLButtonElement,
) => {
  // Individual extras already on the booking cannot be replaced with catalog options.
  if (customExtra) return;

  const comparedOption = props.booking.priceCalculation.availableExtras
    ?.find((ae) => ae.type === extra.type)
    ?.options.find((o) => o.id === changedBookedExtraTypes.value[extra.type]?.vehicleExtraOptionId);
  if (
    !changedBookedExtraTypes.value[extra.type]?.vehicleExtraOptionId ||
    isBiggerExtraOption(option, comparedOption!, extra)
  ) {
    fireConfetti(buttonElement);
  }

  if (extra.type === VehicleExtraType.EXTRA_KM) {
    customKmInput.value = null;
    customKmValue.value = 0;
  }

  if (changedBookedExtraTypes.value[extra.type]?.vehicleExtraOptionId === option.id) {
    changedBookedExtraTypes.value[extra.type] = undefined;
  } else {
    changedBookedExtraTypes.value[extra.type] = !isBookedOption
      ? {
          vehicleExtraId: extra.id,
          vehicleExtraOptionId: option.id,
        }
      : undefined;
  }
  if (extra.type === VehicleExtraType.DEDUCTIBLE && option.value === '0') {
    delete changedBookedExtraTypes.value[VehicleExtraType.GLASS_TYRE_WAIVER_AND_MOBILITY];

    if (
      changedBookedExtraTypes.value[VehicleExtraType.DEDUCTIBLE]?.vehicleExtraOptionId === option.id
    ) {
      const bookedTrue =
        getBookedExtras(props.booking.priceCalculation)?.find(
          (bookedExtra) =>
            bookedExtra.vehicleExtraSnapshot.type ===
            VehicleExtraType.GLASS_TYRE_WAIVER_AND_MOBILITY,
        )?.vehicleExtraOptionSnapshot.value === 'true';
      const windowExtra = props.booking.priceCalculation.availableExtras?.find(
        (availableExtra) => availableExtra.type === VehicleExtraType.GLASS_TYRE_WAIVER_AND_MOBILITY,
      );
      const falseOption = windowExtra?.options.find((o) => o.value === 'false');
      if (bookedTrue && windowExtra && falseOption) {
        changedBookedExtraTypes.value[VehicleExtraType.GLASS_TYRE_WAIVER_AND_MOBILITY] = {
          vehicleExtraId: windowExtra.id,
          vehicleExtraOptionId: falseOption.id,
        };
      }
    }
  }

  changedBookedExtraTypes.value = pickBy(
    changedBookedExtraTypes.value,
    (value) => value !== undefined,
  );

  await recalculate();
};

const saveExtrasChange = async () => {
  if (!newPricing.value) return;
  const { status } = await confirmAndSave({
    booking: props.booking,
    priceDifferenceCounterparty: CounterpartyType.CUSTOMER,
    handoverAdjustment: props.handoverAdjustment,
    confirmOnlyWhenCancellationFee: true,
    updateBookingDto: {
      pricing: {
        priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
        bookedExtras: extraUpdates.value,
        customBookedExtras: customBookedExtras.value,
      },
    },
  });
  if (status !== 'saved') return;
  changedBookedExtraTypes.value = {};
};

const cancelExtrasChange = () => {
  changedBookedExtraTypes.value = {};
  newPricing.value = null;
  if (
    existingCustomExtraKm.value?.value &&
    existingCustomExtraKm.value.value > maxExtraKmValue.value
  ) {
    customKmInput.value = existingCustomExtraKm.value.value;
    customKmValue.value = existingCustomExtraKm.value.value;
  } else {
    customKmInput.value = null;
    customKmValue.value = 0;
  }
};

watch(hasChanged, (newValue) => emit('hasChanged', newValue));
</script>

<i18n lang="json">
{
  "en": {
    "upgradeExtras": "Upgrade Extras",
    "no": "No",
    "yes": "Yes",
    "day": "Day",
    "noOption": "No",
    "newPrice": "New Price",
    "recalculatingPrice": "Recalculating Price...",
    "extrasKmInfo": "Each additional km will be charged with {price}",
    "showChildSeats": "Show Child Seats",
    "customKmMinError": "Min. {min} km",
    "customExtraKmDescription": "Individual extra km package ({km} km)",
    "individual": "Individual",
    "cancel": "Cancel",
    "included": "incl."
  },
  "de": {
    "upgradeExtras": "Extras upgraden",
    "no": "Nein",
    "yes": "Ja",
    "day": "Tag",
    "noOption": "Kein",
    "newPrice": "Neuer Preis",
    "recalculatingPrice": "Preis wird neu berechnet...",
    "extrasKmInfo": "Jeder zusätzliche Km wird mit {price} berechnet",
    "showChildSeats": "Kindersitze anzeigen",
    "customKmMinError": "Min. {min} km",
    "customExtraKmDescription": "Individuelles Extrakilometerpaket ({km} km)",
    "individual": "Individuell",
    "cancel": "Abbrechen",
    "included": "inkl."
  },
  "es": {
    "upgradeExtras": "Mejorar extras",
    "no": "No",
    "yes": "Sí",
    "day": "Día",
    "noOption": "Ninguno",
    "newPrice": "Nuevo precio",
    "recalculatingPrice": "Recalculando el precio…",
    "extrasKmInfo": "Cada km adicional se facturará a {price}",
    "showChildSeats": "Mostrar sillas infantiles",
    "customKmMinError": "Mín. {min} km",
    "customExtraKmDescription": "Paquete de km adicionales a medida ({km} km)",
    "individual": "A medida",
    "cancel": "Cancelar",
    "included": "incl."
  }
}
</i18n>
